import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Button } from 'ui-components/components/button';
import { FlexRow } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { emptyArray } from 'ui-components/default-props/empty';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { useEnablePickToTote } from '../../../../feature-flags/hooks/use-pick-to-tote';
import { useModal } from '../../../../hooks/use-modal';
import Global from '../../../../store/global-selectors';
import { ConfigureWarehouseDrawer } from '../configure-warehouse-drawer';
import ActionButtons from './action-buttons';
import style from '../inventory-warehouses.module.less';
import messages from '../messages';
import { LinkedShipLocationTooltip } from './tooltip/tooltip';

export const InventoryWarehousesGrid = () => {
  const inventoryWarehouses = useSelector(Global.selectSellerInventoryWarehouses) || emptyArray;
  const sellerShipFrom = useSelector(Global.selectSellerShipFroms) || emptyArray;
  const allWarehouseLocations = useSelector((state) => state.seller?.data?.inventoryWarehousesLocations) || emptyArray;
  const allTotes = useSelector(Global.selectSellerTotes) || emptyArray;
  const isTotesLoading = useSelector(Global.selectSellerTotesIsLoading);
  const isTotesTabEnabled = useEnablePickToTote();
  const { isOpen: isConfigureWarehouseOpen, open: openConfigureWarehouse, close: closeConfigureWarehouse } = useModal();
  const [activeInventoryWarehouse, setActiveInventoryWarehouse] = useState({
    id: '',
    name: '',
  });

  /**
   * @param {string} inventoryWarehouseId
   * @param {string} inventoryWarehouseName
   */
  function handleOpenConfigureWarehouseDrawer(inventoryWarehouseId, inventoryWarehouseName) {
    setActiveInventoryWarehouse({
      id: inventoryWarehouseId,
      name: inventoryWarehouseName,
    });
    openConfigureWarehouse();
  }

  const openConfigureWarehouseDrawer = useCallback(handleOpenConfigureWarehouseDrawer, [openConfigureWarehouse]);

  const updateActiveWarehouseName = useCallback((newName) => {
    setActiveInventoryWarehouse((prev) => ({ ...prev, name: newName }));
  }, []);

  const warehousesWithLocations = inventoryWarehouses.map((warehouse) => {
    const matchingLocations = sellerShipFrom.filter((location) => location.inventorySourceId === warehouse.inventoryWarehouseId);

    const warehouseLocations = allWarehouseLocations.filter((location) => location.inventoryWarehouseId === warehouse.inventoryWarehouseId);

    const totesCount = allTotes.filter((tote) => tote.inventoryWarehouseId === Number(warehouse.inventoryWarehouseId)).length;

    return {
      ...warehouse,
      matchingLocations,
      warehouseLocationsCount: warehouseLocations.length,
      totesCount,
    };
  });

  const renderLocationLinks = (locations) => {
    if (locations.length > 1) {
      return (
        <LinkedShipLocationTooltip
          buttonText={<FormattedMessage {...messages.shipFromsCount} values={{ count: locations.length }} />}
          locations={locations}
        />
      );
    }

    if (locations.length === 0) {
      return '-';
    }

    const location = locations[0];
    return (
      <Link key={location.shipFromId} className={style['link-button']} to='/settings/warehouses'>
        {location.name}
      </Link>
    );
  };

  if (isTotesTabEnabled && isTotesLoading && allTotes.length === 0) {
    return (
      <FlexRow className={style['loading-container']}>
        <LoadingIndicator className={style['loading-indicator']} />
      </FlexRow>
    );
  }

  return (
    <div
      className={classNames(style.grid, {
        [style['grid--with-totes']]: isTotesTabEnabled,
        [style['grid--no-totes']]: !isTotesTabEnabled,
      })}
    >
      <div className={style['grid-header']}>
        <div className={style['name-row']}>
          <FormattedMessage {...messages.warehousesGridHeaderNameColumn} />
        </div>
        {isTotesTabEnabled ? (
          <div className={style['locations-row']}>
            <FormattedMessage {...messages.warehousesGridHeaderLocationsColumn} />
          </div>
        ) : null}
        {isTotesTabEnabled ? (
          <div className={style['totes-row']}>
            <FormattedMessage {...messages.warehousesGridHeaderTotesColumn} />
          </div>
        ) : null}
        <div className={style['is-linked-row']}>
          <FormattedMessage {...messages.warehousesGridHeaderIsLinkedColumn} />
        </div>
        <div className={style['action-buttons-wrapper']}>
          <FormattedMessage {...messages.warehousesGridHeaderActionsColumn} />
        </div>
      </div>
      {warehousesWithLocations.map(({ name, inventoryWarehouseId, matchingLocations, warehouseLocationsCount, totesCount }) => (
        <div key={inventoryWarehouseId} className={style.row}>
          <div className={style['name-row']} title={name}>
            <Button onClick={() => openConfigureWarehouseDrawer(inventoryWarehouseId, name)} theme={THEME_CONSTANTS.link}>
              {name}
            </Button>
          </div>
          {isTotesTabEnabled ? (
            <div className={style['locations-row']}>{warehouseLocationsCount > 1 ? warehouseLocationsCount - 1 : '-'}</div>
          ) : null}
          {isTotesTabEnabled ? <div className={style['totes-row']}>{totesCount || '-'}</div> : null}
          <div className={style['is-linked-row']}>{renderLocationLinks(matchingLocations)}</div>
          <div className={style['action-buttons-wrapper']}>
            <ActionButtons inventoryWarehouseId={inventoryWarehouseId} inventoryWarehouseName={name} />
          </div>
        </div>
      ))}

      {isConfigureWarehouseOpen && activeInventoryWarehouse ? (
        <ConfigureWarehouseDrawer
          changeWarehouse={(id, name) => {
            setActiveInventoryWarehouse({ id, name });
          }}
          inventoryWarehouseId={activeInventoryWarehouse?.id}
          onClose={closeConfigureWarehouse}
          onWarehouseNameUpdate={updateActiveWarehouseName}
          warehouseName={activeInventoryWarehouse?.name}
        />
      ) : null}
    </div>
  );
};
